import { useState, useEffect } from 'react';
import { Icon } from './Icon';
import { api } from '../../utils/api';

interface ControlItem {
  control: string;
  section: string;
  objective: string;
}

interface RecommendedControl {
  controlNumber: string;
  reason: string;
  matchedControl: ControlItem | null;
}

interface AiControlSuggestProps {
  context: {
    assetCategory: string;
    threat: string;
    vulnerability: string;
  };
  allControls: ControlItem[];
  /** Controls already added to the risk (by control name) */
  existingControls?: string[];
  onSelectControls: (controlNames: string[]) => void;
  className?: string;
}

/**
 * Parses the AI response into an array of recommended controls.
 * Expects JSON array of { control: "5.15", reason: "..." } objects.
 * Falls back gracefully if parsing fails.
 */
function parseRecommendations(text: string): Array<{ control: string; reason: string }> {
  // Try to extract JSON array from the response
  const trimmed = text.trim();

  // Try direct parse first
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // Not direct JSON
  }

  // Try to find JSON array within the text
  const match = trimmed.match(/\[[\s\S]*?\]/);
  if (match) {
    try {
      const parsed = JSON.parse(match[0]);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // Could not parse extracted array
    }
  }

  return [];
}

export function AiControlSuggest({
  context,
  allControls,
  existingControls = [],
  onSelectControls,
  className = '',
}: AiControlSuggestProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recommendations, setRecommendations] = useState<RecommendedControl[]>([]);
  const [rawText, setRawText] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Close on Escape key
  useEffect(() => {
    if (!showPopup) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowPopup(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showPopup]);

  const isDisabled = !context.assetCategory || !context.threat || !context.vulnerability;

  const handleClick = async () => {
    setLoading(true);
    setError('');
    setRecommendations([]);
    setRawText('');
    setSelected(new Set());
    try {
      const result = await api.post('/ai/suggest', {
        type: 'control-recommendation',
        context: {
          assetCategory: context.assetCategory,
          threat: context.threat,
          vulnerability: context.vulnerability,
        },
      }) as { suggestion: string };

      if (result.suggestion) {
        setRawText(result.suggestion);
        const parsed = parseRecommendations(result.suggestion);

        if (parsed.length > 0) {
          // Match each recommendation against the actual controls list
          const matched: RecommendedControl[] = parsed.map((rec) => {
            const controlNum = rec.control?.trim();
            // Find control that starts with this number (e.g. "5.15" matches "5.15 Access control")
            const match = allControls.find((c) =>
              c.control.startsWith(controlNum + ' ')
            );
            return {
              controlNumber: controlNum,
              reason: rec.reason || '',
              matchedControl: match || null,
            };
          });
          setRecommendations(matched);
        }
        setShowPopup(true);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'AI suggestion failed';
      setError(message);
      setTimeout(() => setError(''), 4000);
    } finally {
      setLoading(false);
    }
  };

  const toggleControl = (controlName: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(controlName)) {
        next.delete(controlName);
      } else {
        next.add(controlName);
      }
      return next;
    });
  };

  const handleAddSelected = () => {
    if (selected.size > 0) {
      onSelectControls(Array.from(selected));
    }
    setShowPopup(false);
  };

  const handleDismiss = () => {
    setShowPopup(false);
  };

  const selectableCount = recommendations.filter(
    r => r.matchedControl && !existingControls.includes(r.matchedControl.control)
  ).length;

  return (
    <>
      <div className="relative inline-flex">
        <button
          type="button"
          onClick={handleClick}
          disabled={loading || isDisabled}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
            loading
              ? 'bg-purple-100 text-purple-400 cursor-wait'
              : isDisabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 shadow-sm hover:shadow'
          } ${className}`}
          title={isDisabled ? 'Select asset category, threat, and vulnerability first' : 'AI recommends applicable controls'}
        >
          {loading ? (
            <Icon name="Loader" size={14} className="animate-spin" />
          ) : (
            <Icon name="Sparkles" size={14} />
          )}
          {loading ? 'Analysing...' : 'AI Suggest Controls'}
        </button>
        {error && (
          <div className="absolute top-full left-0 mt-1 z-10 px-3 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg whitespace-nowrap">
            {error}
          </div>
        )}
      </div>

      {/* Recommended Controls Multi-Select Popup */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleDismiss}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-purple-500 to-indigo-500">
              <div className="flex items-center gap-2 text-white">
                <Icon name="Sparkles" size={18} />
                <h3 className="text-base font-semibold">Recommended Controls</h3>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-white/80 hover:text-white transition-colors"
                title="Dismiss"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="px-5 py-4">
              <p className="text-sm text-gray-500 mb-3">
                Based on <strong>{context.threat}</strong> and <strong>{context.vulnerability}</strong>. Select one or more controls to add.
              </p>

              {recommendations.length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {recommendations.map((rec, idx) => {
                    const controlName = rec.matchedControl?.control || '';
                    const alreadyAdded = existingControls.includes(controlName);
                    const isSelected = selected.has(controlName);
                    const canSelect = rec.matchedControl && !alreadyAdded;

                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => canSelect && toggleControl(controlName)}
                        disabled={!canSelect}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          alreadyAdded
                            ? 'border-green-200 bg-green-50 opacity-70 cursor-default'
                            : isSelected
                              ? 'border-purple-400 bg-purple-100 ring-2 ring-purple-300 cursor-pointer'
                              : rec.matchedControl
                                ? 'border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-300 cursor-pointer'
                                : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            {/* Checkbox indicator */}
                            {canSelect && (
                              <div className={`mt-0.5 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                                isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300 bg-white'
                              }`}>
                                {isSelected && (
                                  <Icon name="Check" size={12} className="text-white" />
                                )}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900">
                                {rec.matchedControl?.control || `Control ${rec.controlNumber}`}
                              </div>
                              {rec.matchedControl?.objective && (
                                <div className="text-xs text-gray-500 mt-0.5 truncate">
                                  {rec.matchedControl.objective.substring(0, 100)}{rec.matchedControl.objective.length > 100 ? '...' : ''}
                                </div>
                              )}
                              {rec.reason && (
                                <div className="text-xs text-purple-700 mt-1 italic">
                                  {rec.reason}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            {rec.matchedControl && (
                              <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                                {rec.matchedControl.section}
                              </span>
                            )}
                            {alreadyAdded && (
                              <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                                Already added
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* Fallback: show raw text if parsing failed */
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap max-h-60 overflow-y-auto">
                  {rawText}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-t border-gray-200">
              <span className="text-xs text-gray-500">
                {selected.size > 0
                  ? `${selected.size} selected`
                  : selectableCount > 0
                    ? `${selectableCount} available to add`
                    : 'All controls already added'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                {selected.size > 0 && (
                  <button
                    type="button"
                    onClick={handleAddSelected}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Add Selected ({selected.size})
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
